import { Typography } from 'antd';
import { CheckOutlined, FireOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { resolvePlanIcon, buildHeroGradient } from './planTheme';
import FeatureCheckRow from './FeatureCheckRow';
import CheckoutButton from './CheckoutButton';
import type { SubscriptionPlan, PlanPriceOption } from '../../types/business';

const { Text } = Typography;

function formatPrice(tiyin: number): string {
  return Math.round(tiyin / 100).toLocaleString('uz-UZ');
}

interface Props {
  plan: SubscriptionPlan;
  /** Tanlangan davr (oy son) */
  selectedDurationMonths: number;
  /** Joriy tarif belgilashi */
  isCurrent: boolean;
  /** Loading holatida tugma spinner ko'rsatadi */
  checkoutLoading: boolean;
  onCheckout: (planCode: string) => void;
}

/**
 * Plan kartochkasi — to'liq BE'dan kelayotgan ma'lumotlar bilan ishlaydi:
 * - theme (rang, ikon)
 * - prices (har duration uchun)
 * - features (BE belgilagan qatorlar)
 * - cta (enabled/label/disabledReason)
 * - popular (Eng mashhur ribbon)
 */
export default function PlanCard({
  plan,
  selectedDurationMonths,
  isCurrent,
  checkoutLoading,
  onCheckout,
}: Props) {
  const { t } = useTranslation('owner');
  const icon = resolvePlanIcon(plan.theme.iconKind);
  const heroGradient = buildHeroGradient(plan.theme);

  // Selected price option — agar BE'da yo'q bo'lsa, birinchisini ishlatamiz
  const priceOption: PlanPriceOption =
    plan.prices.find((p) => p.durationMonths === selectedDurationMonths) ?? plan.prices[0];

  const isFreePlan = priceOption && priceOption.priceTiyin === 0;

  return (
    <div style={{ position: 'relative', paddingTop: plan.popular ? 14 : 0 }}>
      {/* Popular ribbon — BE controls visibility via `popular` flag */}
      {plan.popular && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            background: `linear-gradient(135deg, ${plan.theme.accent} 0%, ${plan.theme.accentDark} 100%)`,
            color: '#fff',
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: 0.4,
            padding: '4px 12px',
            borderRadius: 999,
            boxShadow: `0 4px 12px ${plan.theme.accent}50`,
            textTransform: 'uppercase',
          }}
        >
          <FireOutlined style={{ fontSize: 10 }} />
          {t('subscription.popular')}
        </div>
      )}

      <div
        style={{
          position: 'relative',
          borderRadius: 18,
          background: 'var(--bl-surface, #fff)',
          border: isCurrent
            ? `2px solid ${plan.theme.accent}`
            : plan.popular
              ? `1px solid ${plan.theme.accent}40`
              : '1px solid var(--bl-divider, #ebebeb)',
          overflow: 'hidden',
          boxShadow: plan.popular
            ? `0 8px 24px ${plan.theme.accent}1f`
            : 'var(--bl-shadow-soft, 0 2px 10px rgba(0,0,0,0.04))',
          transition: 'box-shadow 0.2s ease',
        }}
      >
        {/* Hero header */}
        <div
          style={{
            position: 'relative',
            background: heroGradient,
            padding: '16px 16px 14px',
            overflow: 'hidden',
          }}
        >
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: -50,
              right: -30,
              width: 130,
              height: 130,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.10)',
              filter: 'blur(8px)',
              pointerEvents: 'none',
            }}
          />

          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 13,
                  background: 'rgba(255,255,255,0.22)',
                  border: '1.5px solid rgba(255,255,255,0.36)',
                  color: '#fff',
                  fontSize: 20,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                }}
              >
                {icon}
              </div>
              <div style={{ minWidth: 0 }}>
                <Text
                  style={{
                    display: 'block',
                    fontSize: 18,
                    fontWeight: 800,
                    color: '#fff',
                    letterSpacing: -0.3,
                    lineHeight: 1.15,
                  }}
                >
                  {plan.displayName}
                </Text>
                {plan.subtitle && (
                  <Text
                    style={{
                      display: 'block',
                      fontSize: 12,
                      color: 'rgba(255,255,255,0.78)',
                      marginTop: 2,
                    }}
                  >
                    {plan.subtitle}
                  </Text>
                )}
              </div>
            </div>

            {isCurrent && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 10,
                  fontWeight: 800,
                  color: '#fff',
                  background: 'rgba(255,255,255,0.20)',
                  border: '1px solid rgba(255,255,255,0.28)',
                  borderRadius: 999,
                  padding: '3px 9px',
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  flexShrink: 0,
                }}
              >
                <CheckOutlined style={{ fontSize: 9 }} />
                {t('subscription.current')}
              </span>
            )}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '14px 16px 16px' }}>
          {/* Price block */}
          {priceOption && (
            <div style={{ marginBottom: 12 }}>
              {isFreePlan ? (
                <Text
                  style={{
                    fontSize: 26,
                    fontWeight: 800,
                    color: 'var(--bl-text-strong, #0f172a)',
                    letterSpacing: -0.5,
                  }}
                >
                  {t('subscription.price.free')}
                </Text>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, flexWrap: 'wrap' }}>
                    <Text
                      style={{
                        fontSize: 28,
                        fontWeight: 800,
                        color: 'var(--bl-text-strong, #0f172a)',
                        letterSpacing: -0.6,
                        lineHeight: 1,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {formatPrice(priceOption.priceTiyin)}
                    </Text>
                    <Text
                      style={{
                        fontSize: 13,
                        color: 'var(--bl-text-muted, #9ca3af)',
                        fontWeight: 500,
                      }}
                    >
                      {priceOption.durationMonths === 12
                        ? t('subscription.price.perYear')
                        : t('subscription.price.perDuration', { months: priceOption.durationMonths })}
                    </Text>
                  </div>

                  {(priceOption.monthlyEquivalentTiyin || priceOption.originalPriceTiyin || priceOption.discountPercent) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                      {priceOption.monthlyEquivalentTiyin && (
                        <Text
                          style={{
                            fontSize: 12,
                            color: 'var(--bl-text-muted, #6b7280)',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {t('subscription.price.perMonth', {
                            price: formatPrice(priceOption.monthlyEquivalentTiyin),
                          })}
                        </Text>
                      )}
                      {priceOption.originalPriceTiyin && priceOption.originalPriceTiyin > priceOption.priceTiyin && (
                        <Text
                          style={{
                            fontSize: 11,
                            color: 'var(--bl-text-muted, #cbd5e1)',
                            textDecoration: 'line-through',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {t('subscription.price.fullStrike', {
                            price: formatPrice(priceOption.originalPriceTiyin),
                          })}
                        </Text>
                      )}
                      {priceOption.discountPercent && priceOption.discountPercent > 0 && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 800,
                            color: '#fff',
                            background: 'linear-gradient(135deg, #1f8b4c 0%, #27ae60 100%)',
                            borderRadius: 6,
                            padding: '2px 7px',
                            letterSpacing: 0.3,
                            boxShadow: '0 2px 6px rgba(31,139,76,0.30)',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {t('subscription.saveAmount', { percent: priceOption.discountPercent })}
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Divider */}
          <div
            style={{
              height: 1,
              background: 'var(--bl-divider, #ebebeb)',
              marginBottom: 12,
            }}
          />

          {/* Features — BE belgilagan ro'yxat */}
          <div style={{ marginBottom: plan.cta.enabled || plan.cta.disabledReason ? 16 : 0 }}>
            {plan.features.map((feature) => (
              <FeatureCheckRow
                key={feature.code}
                label={feature.label}
                value={feature.value ?? undefined}
                enabled={feature.enabled}
              />
            ))}
          </div>

          {/*
            CTA — BE'dan kelgan cta'ni hurmat qiladi.
            - enabled=true  → actionable tugma (Sotib olish / Yangilash)
            - disabledReason → informational disabled tugma (Joriy reja / DOWNGRADE_NOT_ALLOWED)
            - ikkalasi ham null/false → tugma yashiriladi (masalan, bo'sh FREE row)
            Joriy plan ham CTA ko'rsata oladi — EXPIRED_IN_GRACE da "Yangilash" tugmasi shu yerdan keladi.
          */}
          {(plan.cta.enabled || plan.cta.disabledReason) && (
            <CheckoutButton
              label={plan.cta.label}
              accent={plan.theme.accent}
              accentDark={plan.theme.accentDark}
              loading={checkoutLoading}
              enabled={plan.cta.enabled}
              disabledReason={plan.cta.disabledReason}
              onClick={() => onCheckout(plan.code)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
